// Re-export all types and constants
export * from './constants';
export * from './menuItems';

// API Response Types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

// Authentication Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface User {
  id: string;
  email: string;
  name: string;
  organizationID: string;
}

export interface JWTPayload {
  iss: string;
  sub: string;
  aud: string[];
  exp: number;
  nbf: number;
  iat: number;
  jti: string;
  _id: string;
  email: string;
  name: string;
  organizationID: string;
}

// Data Types
export interface Author {
  id: string;
  name: string;
}

export interface Definition {
  _id: string;
  title: string;
  author: Author;
  description: string;
  definition: string;
  organization: string;
  definitionURL: string;
  deprecated: boolean;
  ancestors?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface DefinitionError {
  ErrorType: number;
  Line: number;
  Column: number;
  SpreadsheetName: string;
  Message: string;
  Level: number;
}

export interface DefinitionErrorResponse {
  errors: DefinitionError[];
  message: string;
  success: false;
}

export interface DataBuild {
  _id: string;
  title: string;
  author: Author;
  description: string;
  spreadsheet: string;
  organizationID: string;
  definitionID: string;
  logs: LogEntry[];
  status: BuildStatus;
  executionTime: number;
  gamedataURL: string;
  bundles: any;
  jsonBundles?: any;
  buildFormats?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface NewBuildRequest {
  title: string;
  description: string;
  datasourceId: string;
  DefinitionID: string;
  BuildJSON?: boolean;
}

// Component Props Types
export interface LayoutProps {
  children: React.ReactNode;
}

export interface AuthProviderProps {
  children: React.ReactNode;
}

// Navigation Types
export interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactElement;
}

// Form Types
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'textarea' | 'checkbox';
  required?: boolean;
  placeholder?: string;
  rows?: number;
}

// Status Types
export type BuildStatus = 'success' | 'running' | 'failed' | 'pending';

// Log Types
export enum LogType {
  INFO = 0,
  WARNING = 1,
  ERROR = 2
}

export interface LogEntry {
  Message: string;
  StructName: string;
  Type: LogType;
}

// Configuration Types
export interface BuildConfiguration {
  enableValidation?: boolean;
  includeMetadata?: boolean;
  generateReports?: boolean;
}

// Environment Types
export interface Environment {
  _id: string;
  name: string;
  description?: string;
  publishedDataID?: string;
  dataPublisher?: {
    id: string;
    name: string;
  };
  publishedDefinitionID?: string;
  definitionPublisher?: {
    id: string;
    name: string;
  };
  organizationID: string;
  createdAt: string;
  updatedAt: string;
}

export interface HistoricalChange {
  action: string;
  authorId: string;
  authorName: string;
  previousValue: string;
  newValue: string;
  timestamp: string;
}

export interface EnvironmentDetails {
  environmentName: string;
  environmentDescription?: string;
  dataPublisher?: {
    id: string;
    name: string;
  };
  definitionPublisher?: {
    id: string;
    name: string;
  };
  environmentCreatedAt: string;
  environmentUpdatedAt: string;
  requirePassword?: boolean;
  password?: string;
  environmentConfigs?: {
    deploymentPassword?: string;
  };
  Gamedata?: {
    id: string;
    title: string;
    author: string;
    description: string;
    definitionId: string;
    gamedataURL: string;
    bundles: Record<string, string>;
    createdAt: string;
  };
  Definition?: {
    id?: string;
    title: string;
    author: string;
    description: string;
    createdAt: string;
  };
  historicalChanges?: HistoricalChange[];
}

export interface CreateEnvironmentRequest {
  name: string;
  description?: string;
  DefinitionID?: string;
  GamedataID?: string;
}

export interface UpdateEnvironmentRequest {
  DefinitionID?: string;
  GamedataID?: string;
  requirePassword?: boolean;
  password?: string;
  deploymentPassword?: string;
}

// Organization Types
export interface Organization {
  organizationName: string;
  hasCredentials: boolean;
  gServiceAccountEmail?: string;
  numberOfCreatedData: number;
  numberOfCreatedDefinitions: number;
  hasClankerURL?: boolean;
  config?: {
    buildFormats?: {
      JSON?: boolean;
    };
  };
}

// Data Source Types
export interface DataSource {
  _id: string;
  title: string;
  description?: string;
  datasourceURL: string;
  organizationID?: string;
  author?: Author | string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDataSourceRequest {
  title: string;
  description?: string;
  datasourceURL: string;
}
